const SHA256_INITIAL_HASH = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
]);

const SHA256_ROUND_CONSTANTS = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5,
    0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc,
    0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
    0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3,
    0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
    0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
    0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function rotateRight(value: number, shift: number): number {
    return (value >>> shift) | (value << (32 - shift));
}

function bytesToHex(bytes: Uint8Array): string {
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

export function randomUuid(): string {
    if (typeof globalThis.crypto.randomUUID === 'function') {
        return globalThis.crypto.randomUUID();
    }
    const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
    const hex = bytesToHex(bytes);
    return [
        hex.slice(0, 8),
        hex.slice(8, 12),
        hex.slice(12, 16),
        hex.slice(16, 20),
        hex.slice(20),
    ].join('-');
}

function sha256WithoutWebCrypto(data: Uint8Array): string {
    const bitLength = data.length * 8;
    const paddedLength = Math.ceil((data.length + 9) / 64) * 64;
    const padded = new Uint8Array(paddedLength);
    padded.set(data);
    padded[data.length] = 0x80;

    const paddedView = new DataView(padded.buffer);
    paddedView.setUint32(paddedLength - 8, Math.floor(bitLength / 0x100000000));
    paddedView.setUint32(paddedLength - 4, bitLength >>> 0);

    const hash = new Uint32Array(SHA256_INITIAL_HASH);
    const schedule = new Uint32Array(64);
    for (let offset = 0; offset < paddedLength; offset += 64) {
        for (let index = 0; index < 16; index += 1) {
            schedule[index] = paddedView.getUint32(offset + index * 4);
        }
        for (let index = 16; index < 64; index += 1) {
            const previous15 = schedule[index - 15] ?? 0;
            const previous2 = schedule[index - 2] ?? 0;
            const sigma0 = (
                rotateRight(previous15, 7)
                ^ rotateRight(previous15, 18)
                ^ (previous15 >>> 3)
            );
            const sigma1 = (
                rotateRight(previous2, 17)
                ^ rotateRight(previous2, 19)
                ^ (previous2 >>> 10)
            );
            schedule[index] = (
                (schedule[index - 16] ?? 0)
                + sigma0
                + (schedule[index - 7] ?? 0)
                + sigma1
            ) >>> 0;
        }

        let a = hash[0] ?? 0;
        let b = hash[1] ?? 0;
        let c = hash[2] ?? 0;
        let d = hash[3] ?? 0;
        let e = hash[4] ?? 0;
        let f = hash[5] ?? 0;
        let g = hash[6] ?? 0;
        let h = hash[7] ?? 0;

        for (let index = 0; index < 64; index += 1) {
            const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
            const choice = (e & f) ^ (~e & g);
            const temp1 = (
                h
                + sum1
                + choice
                + (SHA256_ROUND_CONSTANTS[index] ?? 0)
                + (schedule[index] ?? 0)
            ) >>> 0;
            const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
            const majority = (a & b) ^ (a & c) ^ (b & c);
            const temp2 = (sum0 + majority) >>> 0;

            h = g;
            g = f;
            f = e;
            e = (d + temp1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (temp1 + temp2) >>> 0;
        }

        hash[0] = ((hash[0] ?? 0) + a) >>> 0;
        hash[1] = ((hash[1] ?? 0) + b) >>> 0;
        hash[2] = ((hash[2] ?? 0) + c) >>> 0;
        hash[3] = ((hash[3] ?? 0) + d) >>> 0;
        hash[4] = ((hash[4] ?? 0) + e) >>> 0;
        hash[5] = ((hash[5] ?? 0) + f) >>> 0;
        hash[6] = ((hash[6] ?? 0) + g) >>> 0;
        hash[7] = ((hash[7] ?? 0) + h) >>> 0;
    }

    const result = new Uint8Array(32);
    const resultView = new DataView(result.buffer);
    hash.forEach((value: number, index: number) => {
        resultView.setUint32(index * 4, value);
    });
    return bytesToHex(result);
}

export async function sha256(message: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(message);
    if (globalThis.crypto?.subtle === undefined) {
        return sha256WithoutWebCrypto(data);
    }
    const hashBuffer = await globalThis.crypto.subtle.digest('SHA-256', data);
    return bytesToHex(new Uint8Array(hashBuffer));
}